// src/components/fix-actions/asset-generation-tray.tsx
import { useEffect, useState } from "react";
import {
  X, Loader2, CheckCircle2, Download, Copy, Check, Edit, Save, RefreshCw, Send,
} from "lucide-react";


import type { StrategyArtifact, ArtifactType } from "../../services/fix-actions/optimization-studio-service";
import { SetupModeContent } from "./asset-generation-tray-setup";
import { FullContentRenderer } from "./full-content-renderer";
import { useResponsive } from "@/app/hooks/common/use-breakpoint";
import { Button, Textarea } from "../kit";
import { ClaimCheckBadge, claimCheckState } from "./artifact-claim-check";

// ============================================================================
// TYPES
// ============================================================================

export type TrayMode = "setup" | "generating" | "review";

export interface GenerationConfig {
  brand: string;
  category: string;
  competitors: string[];
  deploymentTargets: {
    ownedSite: boolean;
    thirdPartyBlogs: boolean;
    forums: boolean;
  };
  tone: string;
  riskSensitivity: string;
}

export interface GenerationStep {
  id: string;
  label: string;
  status: "pending" | "active" | "complete";
}

interface AssetGenerationTrayProps {
  mode: TrayMode;
  artifact: StrategyArtifact | null;
  supportingActionTitle: string;
  supportingActionTheme: string;
  artifactType: ArtifactType;
  objective: string;
  outputTypeDescription: string;
  isAssistantCollapsed: boolean;

  onClose: () => void;
  onGenerate: (config: GenerationConfig) => void;
  onApprove: () => void;
  onRegenerate: () => void;

  initialConfig?: Partial<GenerationConfig>;
  ownedSiteLabel?: string;
  thirdPartyBlogsLabel?: string;
  forumsLabel?: string;
  modelsUsed?: string[];
}

// ============================================================================
// HELPERS
// ============================================================================

function mergeGenerationConfig(
  base: GenerationConfig,
  partial?: Partial<GenerationConfig>
): GenerationConfig {
  if (!partial) return base;
  return {
    ...base,
    ...partial,
    competitors: Array.isArray(partial.competitors) ? partial.competitors : base.competitors,
    deploymentTargets: {
      ...base.deploymentTargets,
      ...(partial.deploymentTargets || {}),
    },
  };
}

function getArtifactTypeColor(type: string): string {
  const MAP: Record<string, string> = {
    "Comparison Asset": "var(--alpha-teal)",
    "Decision Framework": "var(--signal-blue)",
    "Scenario-Based Q&A": "var(--spectral-violet)",
    "Boundary / Trust Artifact": "var(--infrared-coral)",
    "Evidence & Citation Pack": "var(--chart-3)",
    "Structured Data / Schema": "var(--muted-foreground)",
    "Data Artifact": "var(--chart-2)",
  };
  return MAP[type] || "var(--muted-foreground)";
}

function downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function flattenToText(obj: any, depth = 0): string {
  const lines: string[] = [];
  const indent = "  ".repeat(depth);
  for (const [k, v] of Object.entries(obj || {})) {
    if (typeof v === "string" && v.trim()) {
      lines.push(`${indent}${k}: ${v}`);
    } else if (Array.isArray(v)) {
      lines.push(`${indent}${k}:`);
      v.forEach((item) => {
        if (typeof item === "string") lines.push(`${indent}  - ${item}`);
        else if (typeof item === "object") lines.push(flattenToText(item, depth + 2));
      });
    } else if (typeof v === "object" && v !== null) {
      lines.push(`${indent}${k}:`);
      lines.push(flattenToText(v, depth + 1));
    }
  }
  return lines.join("\n");
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AssetGenerationTray({
  mode,
  artifact,
  supportingActionTitle,
  supportingActionTheme,
  artifactType,
  objective,
  outputTypeDescription,
  isAssistantCollapsed,
  onClose,
  onGenerate,
  onApprove,
  onRegenerate,
  initialConfig,
  ownedSiteLabel,
  thirdPartyBlogsLabel,
  forumsLabel,
  modelsUsed,
}: AssetGenerationTrayProps) {
  const { isDesktop } = useResponsive();

  const baseConfig: GenerationConfig = {
    brand: "",
    category: "",
    competitors: [],
    deploymentTargets: { ownedSite: true, thirdPartyBlogs: true, forums: false },
    tone: "Neutral",
    riskSensitivity: "Standard",
  };

  const [config, setConfig] = useState<GenerationConfig>(() =>
    mergeGenerationConfig(baseConfig, initialConfig)
  );

  const [isMinimized, setIsMinimized] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [saveConfirmed, setSaveConfirmed] = useState(false);

  const [copied, setCopied] = useState(false);

  const [generationSteps, setGenerationSteps] = useState<GenerationStep[]>([
    {
      id: "step1",
      label: "Analyzing current assistant reasoning patterns in this category",
      status: "pending",
    },
    {
      id: "step2",
      label: "Extracting recurring evaluation criteria and tradeoffs",
      status: "pending",
    },
    {
      id: "step3",
      label: "Constructing a structured, reusable framework",
      status: "pending",
    },
    {
      id: "step4",
      label: "Checking neutrality and deployment readiness",
      status: "pending",
    },
  ]);

  useEffect(() => {
    if (mode !== "generating") return;
    setGenerationSteps((prev) =>
      prev.map((s, idx) => ({ ...s, status: idx === 0 ? "active" : "pending" }))
    );
    let currentStep = 0;
    const interval = setInterval(() => {
      setGenerationSteps((prev) => {
        const updated = [...prev];
        if (currentStep > 0) updated[currentStep - 1].status = "complete";
        if (currentStep < updated.length) {
          updated[currentStep].status = "active";
          currentStep++;
        }
        return updated;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [mode]);

  useEffect(() => {
    if (artifact?.fullContent) {
      setEditedContent(JSON.stringify(artifact.fullContent, null, 2));
      setEditError(null);
    }
  }, [artifact]);

  const typeColor = getArtifactTypeColor(artifactType);
  const safeFilename = (artifact?.name || "asset").replace(/[^a-z0-9_-]/gi, "_");

  const handleCopy = () => {
    if (!artifact) return;
    const text = JSON.stringify(artifact.fullContent, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const handleDownloadJson = () => {
    if (!artifact?.fullContent) return;
    downloadBlob(
      JSON.stringify(artifact.fullContent, null, 2),
      `${safeFilename}.json`,
      "application/json"
    );
  };

  const handleDownloadText = () => {
    if (!artifact?.fullContent) return;
    downloadBlob(flattenToText(artifact.fullContent), `${safeFilename}.txt`, "text/plain");
  };

  const handleSaveEdit = () => {
    setEditError(null);
    try {
      JSON.parse(editedContent);
      setIsEditing(false);
      setSaveConfirmed(true);
      setTimeout(() => setSaveConfirmed(false), 2000);
    } catch {
      setEditError("Invalid JSON — please fix before saving.");
    }
  };

  // Responsive tray sizing (same pattern as ArtifactDetailTray).
  const trayPosStyle: React.CSSProperties = isDesktop
    ? {
        right: isAssistantCollapsed ? "0" : "380px",
        left: "auto",
        width: "50%",
        minWidth: "600px",
        borderLeft: "1px solid var(--border)",
      }
    : {
        right: 0,
        left: 0,
        width: "100%",
        minWidth: 0,
        borderLeft: "none",
      };

  return (
    <>
      {/* Backdrop */}
      {!isMinimized && (
        <div
          className="fixed inset-0 bg-black/20 z-30"
          style={{ backdropFilter: "blur(2px)" }}
          onClick={onClose}
        />
      )}

      {/* Minimised pill */}
      {isMinimized && (
        <div
          className="fixed z-40 rounded-lg shadow-2xl cursor-pointer"
          style={{
            backgroundColor: "var(--card)",
            border: "2px solid var(--signal-blue)",
            bottom: "24px",
            right: isDesktop && !isAssistantCollapsed ? "404px" : "24px",
            left: isDesktop ? "auto" : "24px",
            width: isDesktop ? "320px" : "auto",
            maxWidth: "calc(100vw - 48px)",
            animation: "fadeIn 0.2s ease-out",
          }}
          onClick={() => setIsMinimized(false)}
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              <Loader2
                className="w-5 h-5 animate-spin mt-0.5 shrink-0"
                style={{ color: "var(--signal-blue)" }}
              />
              <div className="flex-1 min-w-0">
                <div
                  style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-semibold)",
                    marginBottom: "4px",
                  }}
                >
                  Generating {artifactType}
                </div>
                <p
                  className="truncate"
                  style={{
                    fontSize: "var(--text-xs)",
                    color: "var(--muted-foreground)",
                    marginBottom: "8px",
                  }}
                >
                  {supportingActionTitle}
                </p>
                <div className="flex items-center gap-2">
                  <div
                    className="flex-1 h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: "var(--border)" }}
                  >
                    <div
                      className="h-full transition-all"
                      style={{
                        backgroundColor: "var(--signal-blue)",
                        width: `${
                          (generationSteps.filter((s) => s.status === "complete").length /
                            generationSteps.length) *
                          100
                        }%`,
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: "var(--text-xs)",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    {generationSteps.filter((s) => s.status === "complete").length}/
                    {generationSteps.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main tray */}
      {!isMinimized && (
        <div
          className="fixed top-0 bottom-0 z-40 overflow-y-auto shadow-2xl"
          style={{
            backgroundColor: "var(--background)",
            animation: "slideInRight 0.3s ease-out",
            ...trayPosStyle,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <TrayHeader
            mode={mode}
            artifactType={artifactType}
            typeColor={typeColor}
            supportingActionTitle={supportingActionTitle}
            supportingActionTheme={supportingActionTheme}
            artifact={artifact}
            saveConfirmed={saveConfirmed}
            onClose={onClose}
          />

          <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6">
            {mode === "setup" && (
              <SetupModeContent
                objective={objective}
                outputTypeDescription={outputTypeDescription}
                artifactType={artifactType}
                config={config}
                setConfig={setConfig}
                ownedSiteLabel={ownedSiteLabel}
                thirdPartyBlogsLabel={thirdPartyBlogsLabel}
                forumsLabel={forumsLabel}
                modelsUsed={modelsUsed}
              />
            )}

            {mode === "generating" && (
              <GeneratingModeContent generationSteps={generationSteps} modelsUsed={modelsUsed} />
            )}

            {mode === "review" && artifact && (
              <ReviewModeContent
                artifact={artifact}
                isEditing={isEditing}
                editedContent={editedContent}
                setEditedContent={setEditedContent}
                editError={editError}
              />
            )}
          </div>

          <TrayFooter
            mode={mode}
            artifact={artifact}
            config={config}
            isEditing={isEditing}
            copied={copied}
            onClose={onClose}
            onGenerate={onGenerate}
            onApprove={onApprove}
            onRegenerate={onRegenerate}
            onCopy={handleCopy}
            onDownloadJson={handleDownloadJson}
            onDownloadText={handleDownloadText}
            onEditToggle={() => {
              setIsEditing((v) => !v);
              setEditError(null);
            }}
            onSaveEdit={handleSaveEdit}
            onMinimize={() => setIsMinimized(true)}
          />
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}

// ============================================================================
// HEADER
// ============================================================================
function TrayHeader({
  mode,
  artifactType,
  typeColor,
  supportingActionTitle,
  supportingActionTheme,
  artifact,
  saveConfirmed,
  onClose,
}: {
  mode: TrayMode;
  artifactType: ArtifactType;
  typeColor: string;
  supportingActionTitle: string;
  supportingActionTheme: string;
  artifact: StrategyArtifact | null;
  saveConfirmed: boolean;
  onClose: () => void;
}) {
  const statusPalette: Record<string, { bg: string; text: string; border: string }> = {
    Generating: {
      bg: "var(--signal-blue-10)",
      text: "var(--signal-blue)",
      border: "var(--signal-blue-30)",
    },
    Draft: {
      bg: "var(--muted)",
      text: "var(--muted-foreground)",
      border: "var(--border)",
    },
    Ready: {
      bg: "var(--success-bg)",
      text: "var(--success-text)",
      border: "var(--success-border)",
    },
    Deployed: {
      bg: "var(--alpha-teal-10)",
      text: "var(--alpha-teal)",
      border: "var(--alpha-teal-20)",
    },
  };

  const statusBadge = (status: string) => {
    const s = statusPalette[status] || statusPalette.Draft;
    return (
      <span
        className="px-2 py-1 rounded"
        style={{
          backgroundColor: s.bg,
          color: s.text,
          fontSize: "var(--text-xs)",
          fontWeight: "var(--font-weight-medium)",
          border: `1px solid ${s.border}`,
        }}
      >
        {status}
      </span>
    );
  };

  return (
    <div
      className="sticky top-0 z-10 px-4 md:px-6 lg:px-8 py-4 md:py-6 border-b"
      style={{ backgroundColor: "var(--background)", borderColor: "var(--border)" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 md:gap-3 mb-2 flex-wrap">
            {mode === "generating" && (
              <Loader2
                className="w-5 h-5 animate-spin shrink-0"
                style={{ color: "var(--signal-blue)" }}
              />
            )}
            <h2
              style={{
                fontSize: "var(--text-lg)",
                fontWeight: "var(--font-weight-semibold)",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {mode === "review" ? artifact?.name : `Generating ${artifactType}`}
            </h2>
            <span
              className="px-2 py-1 rounded shrink-0"
              style={{
                backgroundColor: `${typeColor}10`,
                color: typeColor,
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-weight-medium)",
                border: `1px solid ${typeColor}30`,
              }}
            >
              {artifactType}
            </span>
            {saveConfirmed && (
              <span
                className="px-2 py-0.5 rounded shrink-0"
                style={{
                  backgroundColor: "var(--success-bg)",
                  color: "var(--success-text)",
                  fontSize: "var(--text-xs)",
                  border: "1px solid var(--success-border)",
                }}
              >
                ✓ Saved
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 md:gap-3 mb-3 flex-wrap">
            <span
              style={{
                fontSize: "var(--text-sm)",
                color: "var(--muted-foreground)",
                overflowWrap: "anywhere",
              }}
            >
              Supporting: <strong>{supportingActionTitle}</strong>
            </span>
            {supportingActionTheme && (
              <>
                <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                  •
                </span>
                <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                  {supportingActionTheme}
                </span>
              </>
            )}
          </div>

          {mode === "review" && artifact && (
            <div className="flex items-center gap-3 flex-wrap">
              {statusBadge(artifact.status)}
              <ClaimCheckBadge check={artifact.claimCheck} />
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="p-2 rounded-lg transition-colors hover:bg-muted shrink-0"
          style={{
            color: "var(--muted-foreground)",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// GENERATING MODE
// ============================================================================
function GeneratingModeContent({
  generationSteps,
  modelsUsed,
}: {
  generationSteps: GenerationStep[];
  modelsUsed?: string[];
}) {
  const completedSteps = generationSteps.filter((s) => s.status === "complete").length;
  const totalSteps = generationSteps.length;

  return (
    <div className="space-y-6">
      <div>
        <div
          style={{
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-semibold)",
            color: "var(--muted-foreground)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: "12px",
          }}
        >
          Reasoning Asset Production
        </div>
        <p
          style={{
            fontSize: "var(--text-base)",
            lineHeight: "1.6",
            marginBottom: "8px",
          }}
        >
          Building a structured asset designed to improve how assistants reason about this
          category.
        </p>
        {modelsUsed && modelsUsed.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              Running across:
            </span>
            {modelsUsed.map((m) => (
              <span
                key={m}
                className="px-2 py-0.5 rounded"
                style={{
                  backgroundColor: "var(--signal-blue-10)",
                  color: "var(--signal-blue)",
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-weight-medium)",
                  border: "1px solid var(--signal-blue-30)",
                }}
              >
                {m}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="pl-4" style={{ borderLeft: "3px solid var(--signal-blue)" }}>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div
            style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semibold)" }}
          >
            Analysis & Construction
          </div>
          <span
            className="px-2 py-1 rounded"
            style={{
              backgroundColor: "var(--signal-blue-10)",
              color: "var(--signal-blue)",
              fontSize: "var(--text-xs)",
              fontWeight: "var(--font-weight-medium)",
              border: "1px solid var(--signal-blue-30)",
            }}
          >
            Step {Math.min(completedSteps + 1, totalSteps)} of {totalSteps}
          </span>
        </div>

        <div className="space-y-4 mb-6">
          {generationSteps.map((step) => (
            <div key={step.id} className="flex items-start gap-3">
              <div className="mt-0.5 shrink-0">
                {step.status === "complete" && (
                  <CheckCircle2 className="w-5 h-5" style={{ color: "var(--chart-3)" }} />
                )}
                {step.status === "active" && (
                  <Loader2
                    className="w-5 h-5 animate-spin"
                    style={{ color: "var(--signal-blue)" }}
                  />
                )}
                {step.status === "pending" && (
                  <div
                    className="w-5 h-5 rounded-full border-2"
                    style={{ borderColor: "var(--border)" }}
                  />
                )}
              </div>
              <div
                style={{
                  fontSize: "var(--text-sm)",
                  color:
                    step.status === "pending" ? "var(--muted-foreground)" : "var(--foreground)",
                  lineHeight: "1.6",
                }}
              >
                {step.label}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginTop: "12px",
          }}
        >
          Working…
        </div>
      </div>

      <div
        className="p-4 rounded-lg"
        style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
      >
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", lineHeight: "1.6" }}>
          You can minimize this panel to continue working. The asset will appear in your
          Optimization Studio queue when ready.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// REVIEW MODE
// ============================================================================
function ReviewModeContent({
  artifact,
  isEditing,
  editedContent,
  setEditedContent,
  editError,
}: {
  artifact: StrategyArtifact;
  isEditing: boolean;
  editedContent: string;
  setEditedContent: (v: string) => void;
  editError: string | null;
}) {
  return (
    <div className="space-y-6">
      {/* Claim check card (#132: was the model's own confidence) */}
      <div
        className="p-4 md:p-6 rounded-lg border"
        style={{ backgroundColor: "var(--chart-3-5)", borderColor: "var(--chart-3-10)" }}
      >
        <div className="flex items-start gap-3 md:gap-4 flex-col sm:flex-row">
          <div
            className="w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: "var(--background)",
              border: "3px solid var(--chart-3)",
            }}
          >
            <span
              style={{
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--chart-3)",
              }}
            >
              {claimCheckState(artifact.claimCheck).tone === "ok" ? "\u2713" : "!"}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h3
              style={{
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semibold)",
                marginBottom: "8px",
              }}
            >
              Ready for Review
            </h3>
            <ul className="space-y-1">
              <li style={{ fontSize: "var(--text-sm)", lineHeight: "1.6" }}>
                • Structured for assistant comprehension and citation
              </li>
              <li style={{ fontSize: "var(--text-sm)", lineHeight: "1.6" }}>
                • Language tuned for neutrality and deployability
              </li>
              <li style={{ fontSize: "var(--text-sm)", lineHeight: "1.6" }}>
                • Ready for publishing to selected surfaces
              </li>
            </ul>
            <div
              style={{
                fontSize: "var(--text-sm)",
                color: "var(--muted-foreground)",
                marginTop: "12px",
              }}
            >
              Generated: {artifact.generatedAt ? new Date(artifact.generatedAt).toLocaleString() : "—"}
            </div>
          </div>
        </div>
      </div>

      {artifact.intent && (
        <div>
          <h3
            style={{
              fontSize: "var(--text-base)",
              fontWeight: "var(--font-weight-semibold)",
              marginBottom: "12px",
            }}
          >
            Strategic Intent
          </h3>
          <p
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--foreground)",
              lineHeight: "1.6",
              marginBottom: "12px",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {artifact.intent}
          </p>
          {artifact.aiReasoningBehavior && (
            <div
              className="p-4 rounded-lg"
              style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
            >
              <div
                style={{
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  marginBottom: "8px",
                }}
              >
                AI Reasoning Behavior Influenced:
              </div>
              <p
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--muted-foreground)",
                  lineHeight: "1.6",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {artifact.aiReasoningBehavior}
              </p>
            </div>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3
            style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semibold)" }}
          >
            Full Content
          </h3>
          <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            {isEditing ? "Editing JSON — save when done" : "Rendered preview"}
          </span>
        </div>

        {isEditing ? (
          <div>
            <Textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="upm-code-area"
              state={editError ? "error" : undefined}
              />
            {editError && (
              <p
                style={{
                  fontSize: "var(--text-xs)",
                  color: "var(--error-text)",
                  marginTop: "4px",
                }}
              >
                {editError}
              </p>
            )}
          </div>
        ) : (
          <div
            className="p-4 md:p-6 rounded-lg"
            style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)" }}
          >
            <FullContentRenderer content={artifact.fullContent} />
          </div>
        )}
      </div>

      {artifact.deploymentTargets && artifact.deploymentTargets.length > 0 && (
        <div>
          <h3
            style={{
              fontSize: "var(--text-base)",
              fontWeight: "var(--font-weight-semibold)",
              marginBottom: "8px",
            }}
          >
            Deployment Targets
          </h3>
          <div className="flex flex-wrap gap-2">
            {artifact.deploymentTargets.map((t) => (
              <span
                key={t}
                className="px-2 py-1 rounded"
                style={{
                  backgroundColor: "var(--muted)",
                  fontSize: "var(--text-xs)",
                  color: "var(--foreground)",
                  border: "1px solid var(--border)",
                }}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// FOOTER
// ============================================================================
function TrayFooter({
  mode,
  config,
  isEditing,
  copied,
  onClose,
  onGenerate,
  onApprove,
  onRegenerate,
  onCopy,
  onDownloadJson,
  onDownloadText,
  onEditToggle,
  onSaveEdit,
  onMinimize,
}: {
  mode: TrayMode;
  artifact: StrategyArtifact | null;
  config: GenerationConfig;
  isEditing: boolean;
  copied: boolean;
  onClose: () => void;
  onGenerate: (config: GenerationConfig) => void;
  onApprove: () => void;
  onRegenerate: () => void;
  onCopy: () => void;
  onDownloadJson: () => void;
  onDownloadText: () => void;
  onEditToggle: () => void;
  onSaveEdit: () => void;
  onMinimize: () => void;
}) {
  if (mode === "setup") {
    return (
      <div
        className="sticky bottom-0 px-4 md:px-6 lg:px-8 py-3 md:py-4 border-t flex items-center justify-end gap-2 flex-wrap"
        style={{ backgroundColor: "var(--background)", borderColor: "var(--border)" }}
      >
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <button
          onClick={() => onGenerate(config)}
          className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md"
          style={{
            backgroundColor: "var(--alpha-teal)",
            color: "white",
            fontWeight: "var(--font-weight-semibold)",
            border: "none",
            cursor: "pointer",
          }}
        >
          Generate Asset →
        </button>
      </div>
    );
  }

  if (mode === "generating") {
    return (
      <div
        className="sticky bottom-0 px-4 md:px-6 lg:px-8 py-3 md:py-4 border-t flex items-center justify-end gap-2"
        style={{ backgroundColor: "var(--background)", borderColor: "var(--border)" }}
      >
        <Button variant="ghost" onClick={onMinimize}>
          Minimize
        </Button>
      </div>
    );
  }

  if (mode === "review") {
    if (isEditing) {
      return (
        <div
          className="sticky bottom-0 px-4 md:px-6 lg:px-8 py-3 md:py-4 border-t flex items-center justify-end gap-2 flex-wrap"
          style={{ backgroundColor: "var(--background)", borderColor: "var(--border)" }}
        >
          <Button variant="ghost" onClick={onEditToggle}>
            Cancel Edit
          </Button>
          <button
            onClick={onSaveEdit}
            className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md"
            style={{
              backgroundColor: "var(--signal-blue)",
              color: "white",
              fontWeight: "var(--font-weight-semibold)",
              border: "none",
              cursor: "pointer",
            }}
          >
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      );
    }

    return (
      <div
        className="sticky bottom-0 px-4 md:px-6 lg:px-8 py-3 md:py-4 border-t"
        style={{ backgroundColor: "var(--background)", borderColor: "var(--border)" }}
      >
        {/* Utility row */}
        <div className="flex items-center gap-1 mb-3 flex-wrap">
          <Button
            variant="ghost"
            onClick={onEditToggle}
            icon={Edit}
          >Edit
          </Button>

          <Button variant="ghost" onClick={onCopy}>
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            {copied ? "Copied!" : "Copy JSON"}
          </Button>

          {/* Two downloads, so two controls. A dropdown holding exactly two
              items costs a click and a menu to say what two buttons say. */}
          <Button variant="ghost" icon={Download} onClick={onDownloadJson}>JSON</Button>
          <Button variant="ghost" icon={Download} onClick={onDownloadText}>TXT</Button>
        </div>

        {/* Primary actions — stack on mobile */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-2">
          <Button
            variant="secondary"
            onClick={onRegenerate}
            icon={RefreshCw}
          >Regenerate
          </Button>

          <button
            onClick={onApprove}
            className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md"
            style={{
              backgroundColor: "var(--alpha-teal)",
              color: "white",
              fontWeight: "var(--font-weight-semibold)",
              border: "none",
              cursor: "pointer",
            }}
          >
            <Send className="w-4 h-4" />
            Approve & Deploy
          </button>
        </div>
      </div>
    );
  }

  return null;
}